import React, { useState } from 'react';
import styled from 'styled-components';

const UploadContainer = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const UploadTitle = styled.h3`
  font-size: 24px;
  color: #2c5aa0;
  margin-bottom: 20px;
  text-align: center;
`;

const UploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2c5aa0;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2c5aa0;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const FileUploadArea = styled.div<{ isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? '#2c5aa0' : '#dee2e6'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: ${props => props.isDragOver ? '#f8f9ff' : '#fafafa'};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #2c5aa0;
    background: #f8f9ff;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadText = styled.div`
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
`;

const FileUploadHint = styled.div`
  font-size: 14px;
  color: #999;
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 10px;
`;

const FileIcon = styled.div`
  font-size: 24px;
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 600;
  color: #333;
`;

const FileSize = styled.div`
  font-size: 14px;
  color: #666;
`;

const RemoveButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #c82333;
  }
`;

const UploadButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? '#dee2e6' : '#2c5aa0'};
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.3s ease;

  &:hover {
    background: ${props => props.disabled ? '#dee2e6' : '#1e3a8a'};
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
`;

interface UploadResult {
	success: boolean;
	message?: string;
	error?: string;
}

const AdminProgramUpload: React.FC = () => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [date, setDate] = useState('');
	const [time, setTime] = useState('');
	const [location, setLocation] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

	const handleFileSelect = (file: File) => {
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
		];

		if (!allowedTypes.includes(file.type)) {
			setUploadResult({
				success: false,
				error: 'PDF, DOC, DOCX 파일만 업로드 가능합니다.'
			});
			return;
		}

		if (file.size > 10 * 1024 * 1024) {
			setUploadResult({
				success: false,
				error: '파일 크기는 10MB 이하여야 합니다.'
			});
			return;
		}

		setSelectedFile(file);
		setUploadResult(null);
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleDragOver = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop = (event: React.DragEvent) => {
		event.preventDefault();
		setIsDragOver(false);

		const file = event.dataTransfer.files[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const removeFile = () => {
		setSelectedFile(null);
		setUploadResult(null);
	};

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!title.trim() || !date.trim() || !time.trim() || !location.trim()) {
			setUploadResult({
				success: false,
				error: '필수 항목을 모두 입력해주세요.'
			});
			return;
		}

		setIsUploading(true);
		setUploadResult(null);

		try {
			const formData = new FormData();
			formData.append('title', title);
			formData.append('description', description);
			formData.append('date', date);
			formData.append('time', time);
			formData.append('location', location);
			if (selectedFile) {
				formData.append('file', selectedFile);
			}

			const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
			const apiKey = localStorage.getItem('adminToken') || process.env.REACT_APP_ADMIN_API_KEY || '';

			const response = await fetch(`${backendUrl}/api/upload-program`, {
				method: 'POST',
				headers: {
					'x-api-key': apiKey,
				},
				body: formData,
			});

			const result = await response.json();

			if (result.success) {
				setUploadResult({
					success: true,
					message: '프로그램이 성공적으로 등록되었습니다!'
				});

				// Reset form
				setTitle('');
				setDescription('');
				setDate('');
				setTime('');
				setLocation('');
				setSelectedFile(null);
			} else {
				setUploadResult({
					success: false,
					error: result.error || '등록에 실패했습니다.'
				});
			}
		} catch (error) {
			setUploadResult({
				success: false,
				error: '서버 연결에 실패했습니다.'
			});
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<UploadContainer>
			<UploadTitle>📋 교회 프로그램 등록</UploadTitle>

			<UploadForm onSubmit={handleSubmit}>
				<FormGroup>
					<Label htmlFor="programTitle">프로그램 제목 *</Label>
					<Input
						id="programTitle"
						type="text"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="예: 2025년 여름 성경학교"
						disabled={isUploading}
						required
					/>
				</FormGroup>

				<FormGroup>
					<Label htmlFor="programDescription">설명</Label>
					<TextArea
						id="programDescription"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="프로그램에 대한 간단한 설명을 입력하세요."
						disabled={isUploading}
					/>
				</FormGroup>

				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
					<FormGroup>
						<Label htmlFor="programDate">날짜 *</Label>
						<Input
							id="programDate"
							type="date"
							value={date}
							onChange={(e) => setDate(e.target.value)}
							disabled={isUploading}
							required
						/>
					</FormGroup>

					<FormGroup>
						<Label htmlFor="programTime">시간 *</Label>
						<Input
							id="programTime"
							type="time"
							value={time}
							onChange={(e) => setTime(e.target.value)}
							disabled={isUploading}
							required
						/>
					</FormGroup>
				</div>

				<FormGroup>
					<Label htmlFor="programLocation">장소 *</Label>
					<Input
						id="programLocation"
						type="text"
						value={location}
						onChange={(e) => setLocation(e.target.value)}
						placeholder="예: 본당, 교육관"
						disabled={isUploading}
						required
					/>
				</FormGroup>

				<FormGroup>
					<Label>첨부 파일 (선택사항)</Label>
					<FileUploadArea
						isDragOver={isDragOver}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						onClick={() => document.getElementById('programFileInput')?.click()}
					>
						<FileUploadInput
							id="programFileInput"
							type="file"
							accept=".pdf,.doc,.docx"
							onChange={handleFileChange}
							disabled={isUploading}
						/>
						<FileUploadText>
							클릭하거나 파일을 드래그하여 업로드
						</FileUploadText>
						<FileUploadHint>
							PDF, DOC, DOCX 파일 (최대 10MB)
						</FileUploadHint>
					</FileUploadArea>

					{selectedFile && (
						<SelectedFile>
							<FileIcon>📄</FileIcon>
							<FileInfo>
								<FileName>{selectedFile.name}</FileName>
								<FileSize>{formatFileSize(selectedFile.size)}</FileSize>
							</FileInfo>
							<RemoveButton type="button" onClick={removeFile}>
								제거
							</RemoveButton>
						</SelectedFile>
					)}
				</FormGroup>

				<UploadButton
					type="submit"
					disabled={!title.trim() || !date.trim() || !time.trim() || !location.trim() || isUploading}
				>
					{isUploading ? '등록 중...' : '프로그램 등록'}
				</UploadButton>

				{uploadResult && (
					<StatusMessage type={uploadResult.success ? 'success' : 'error'}>
						{uploadResult.success ? uploadResult.message : uploadResult.error}
					</StatusMessage>
				)}
			</UploadForm>
		</UploadContainer>
	);
};

export default AdminProgramUpload;
